import { css } from '@emotion/react'
import debounce from 'lodash/debounce'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { languageNames } from '@/localization/languageNames'
import {
  FloatingButton,
  FloatingPanel,
  floatingPanelDefaultMiddleware,
  floatingPanelUtils,
  IconMapsGlobe,
  ListSelector,
  mediaQueryHookQueries,
  useMediaQuery,
} from '../..'

const LanguageSelector = () => {
  const { t, i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [selectorHeight, setSelectorHeight] = useState<number>()

  const isMobileView = useMediaQuery(mediaQueryHookQueries.md)

  const setMaxHeightDebounce = debounce(setSelectorHeight, 50)

  const getLanguageOptions = () => {
    const languageOptions = Array.from(languageNames).map(([key, value]) => ({
      value: key,
      name: value[0],
      subName: value[1],
      testDataLabel: key,
    }))
    return languageOptions.sort((a, b) => a.name.localeCompare(b.name))
  }

  const handleLanguageChange = (languageCode?: string) => {
    if (languageCode) {
      i18n
        .changeLanguage(languageCode)
        .catch(() => console.log('Language change failed by i18n'))
        .finally(() => {
          setIsOpen(false)
        })
    }
  }

  return (
    <FloatingPanel
      arrow="wide"
      css={css`
        border-radius: 10px;
        margin-top: 10px;
        width: 432px;
        @media (max-width: 432px) {
          width: 100%;
        }
      `}
      isPortaled
      middleware={[
        ...floatingPanelDefaultMiddleware,
        floatingPanelUtils.size({
          apply({ availableHeight }) {
            setMaxHeightDebounce(availableHeight - 12)
          },
        }),
      ]}
      onOpenChange={() => setIsOpen(!isOpen)}
      open={isOpen}
      reference={
        <FloatingButton
          css={css`
            margin: 0 8px;
            font-weight: normal;
          `}
          icon={<IconMapsGlobe />}
          onClick={() => setIsOpen(!isOpen)}
          testDataLabel="languageSelector-toggle-btn"
          text={isMobileView ? '' : languageNames.get(i18n.language)?.[0]}
        />
      }
    >
      <ListSelector
        css={css`
          padding-inline-end: 10px;
        `}
        header={t('LanguageSelectHeader')}
        items={getLanguageOptions()}
        maxHeight={selectorHeight}
        onItemClick={(item) => handleLanguageChange(item?.value)}
        selectedItem={i18n.language}
        testDataLabel="languageSelector"
      />
    </FloatingPanel>
  )
}

export default LanguageSelector
